/* Real Stickers size guide: draws the chosen sticker to scale on a signboard and builds a
   free SVG artwork template at the real size in millimetres. Runs in the browser only. */
if (!customElements.get('rs-size-guide')) {
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const fmt = (n) => Math.round(n).toLocaleString('en-AU');

  class RsSizeGuide extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-form]');
      this.stage = this.querySelector('[data-stage]');
      this.caption = this.querySelector('[data-caption]');
      this.custom = this.querySelector('[data-custom]');
      this.note = this.querySelector('[data-download-note]');
      this.bleed = parseFloat(this.dataset.bleed) || 0;
      this.safe = parseFloat(this.dataset.safe) || 0;
      if (!this.form || !this.stage) return;

      this.form.addEventListener('change', () => this.render());
      this.form.addEventListener('input', (e) => { if (e.target.type === 'number') this.render(); });
      this.querySelector('[data-download]')?.addEventListener('click', () => this.download(this.size()));
      this.querySelectorAll('[data-get]').forEach((btn) =>
        btn.addEventListener('click', () => {
          const input = this.form.querySelector(`input[name="size"][value="${btn.dataset.get}"]`);
          if (!input) return;
          input.checked = true;
          this.render();
          this.download(this.size());
        })
      );
      this.render();
    }

    size() {
      const input = this.form.querySelector('input[name="size"]:checked') || this.form.querySelector('input[name="size"]');
      if (!input) return null;
      return { shape: input.dataset.shape, w: +input.dataset.w, h: +input.dataset.h, label: input.dataset.label };
    }

    board() {
      const value = this.form.elements.board.value;
      const isCustom = value === 'custom';
      this.custom.hidden = !isCustom;
      if (isCustom) {
        const w = Math.min(6000, Math.max(200, +this.form.elements.board_w.value || 1200));
        const h = Math.min(6000, Math.max(200, +this.form.elements.board_h.value || 900));
        return { w, h };
      }
      const [w, h] = value.split('x').map(Number);
      return { w, h };
    }

    el(name, attrs, text) {
      const n = document.createElementNS(SVG_NS, name);
      for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, v);
      if (text != null) n.textContent = text;
      return n;
    }

    render() {
      const s = this.size();
      const b = this.board();
      if (!s || !b) return;
      const pad = Math.max(b.w, b.h) * 0.08;
      const svg = this.stage;
      svg.setAttribute('viewBox', `${-pad} ${-pad} ${b.w + pad * 2} ${b.h + pad * 2}`);
      svg.replaceChildren();

      const line = Math.max(b.w, b.h) / 300;
      // signboard
      svg.append(this.el('rect', { x: 0, y: 0, width: b.w, height: b.h, rx: line * 2, class: 'rs-sg__board', 'stroke-width': line }));
      svg.append(this.el('rect', { x: 0, y: 0, width: b.w, height: b.h * 0.16, class: 'rs-sg__board-band' }));
      svg.append(this.el('text', { x: b.w / 2, y: b.h * 0.6, 'font-size': Math.min(b.w, b.h) * 0.12, class: 'rs-sg__board-text', 'text-anchor': 'middle' }, 'FOR SALE'));

      // sticker
      const fits = s.w <= b.w && s.h <= b.h;
      const g = this.el('g', { class: 'rs-sg__sticker' + (fits ? '' : ' is-too-big') });
      if (s.shape === 'round') {
        const r = s.w / 2;
        const cx = Math.max(r, b.w - r - b.w * 0.04);
        const cy = Math.min(b.h - r, r + b.h * 0.04);
        g.append(this.el('circle', { cx, cy, r }));
        g.append(this.el('text', { x: cx, y: cy, 'font-size': s.w * 0.26, 'text-anchor': 'middle', 'dominant-baseline': 'central' }, 'SOLD'));
      } else {
        const x = (b.w - s.w) / 2;
        const y = Math.max(b.h * 0.2, 0);
        g.append(this.el('rect', { x, y, width: s.w, height: s.h, rx: Math.min(s.w, s.h) * 0.06 }));
        g.append(this.el('text', { x: b.w / 2, y: y + s.h / 2, 'font-size': s.h * 0.62, 'text-anchor': 'middle', 'dominant-baseline': 'central' }, 'SOLD'));
      }
      svg.append(g);

      // dimension labels
      const fs = Math.max(b.w, b.h) * 0.042;
      const sizeText = s.shape === 'round' ? `${fmt(s.w)} mm round` : `${fmt(s.w)} × ${fmt(s.h)} mm`;
      svg.append(this.el('text', { x: b.w / 2, y: -pad * 0.32, 'font-size': fs, 'text-anchor': 'middle', class: 'rs-sg__dim' }, `Sticker ${sizeText}`));
      svg.append(this.el('text', { x: b.w / 2, y: b.h + pad * 0.72, 'font-size': fs * 0.9, 'text-anchor': 'middle', class: 'rs-sg__dim rs-sg__dim--board' }, `Board ${fmt(b.w)} × ${fmt(b.h)} mm`));

      const pct = Math.round((s.w / b.w) * 100);
      this.caption.textContent = fits
        ? `A ${sizeText} sticker is ${pct}% of the width of a ${fmt(b.w)} × ${fmt(b.h)} mm board.`
        : `A ${sizeText} sticker is bigger than a ${fmt(b.w)} × ${fmt(b.h)} mm board. Try a smaller size.`;
      if (this.note) this.note.textContent = '';
    }

    templateSvg(s) {
      const bl = this.bleed;
      const sf = this.safe;
      const W = s.w + bl * 2;
      const H = s.h + bl * 2;
      const name = s.shape === 'round' ? `${s.w} mm round` : `${s.w} x ${s.h} mm`;
      const fs = Math.max(4, Math.min(s.w, s.h) * 0.045);
      let shapes;
      if (s.shape === 'round') {
        const r = s.w / 2;
        shapes =
          `  <g id="bleed"><circle cx="${r}" cy="${r}" r="${r + bl}" fill="#ffffff" stroke="#e5484d" stroke-width="0.5"/></g>\n` +
          `  <g id="artwork"><!-- Place your artwork here, extended to the red bleed line --></g>\n` +
          `  <g id="trim"><circle cx="${r}" cy="${r}" r="${r}" fill="none" stroke="#121212" stroke-width="0.5"/></g>\n` +
          (sf > 0 && r - sf > 0 ? `  <g id="safe-area"><circle cx="${r}" cy="${r}" r="${r - sf}" fill="none" stroke="#2f80ed" stroke-width="0.5" stroke-dasharray="4 3"/></g>\n` : '');
      } else {
        shapes =
          `  <g id="bleed"><rect x="${-bl}" y="${-bl}" width="${W}" height="${H}" fill="#ffffff" stroke="#e5484d" stroke-width="0.5"/></g>\n` +
          `  <g id="artwork"><!-- Place your artwork here, extended to the red bleed line --></g>\n` +
          `  <g id="trim"><rect x="0" y="0" width="${s.w}" height="${s.h}" fill="none" stroke="#121212" stroke-width="0.5"/></g>\n` +
          (sf > 0 && s.h - sf * 2 > 0 ? `  <g id="safe-area"><rect x="${sf}" y="${sf}" width="${s.w - sf * 2}" height="${s.h - sf * 2}" fill="none" stroke="#2f80ed" stroke-width="0.5" stroke-dasharray="4 3"/></g>\n` : '');
      }
      const cx = s.w / 2;
      const cy = s.h / 2;
      return (
        `<?xml version="1.0" encoding="UTF-8"?>\n` +
        `<!-- Real Stickers artwork template: ${name}. Red = bleed (${bl} mm), black = trim, blue dashed = safe area (${sf} mm).\n` +
        `     Extend backgrounds to the bleed line, keep text and logos inside the safe area, then delete the guides layer\n` +
        `     and export a PDF or PNG at 300 dpi or higher. realstickers.au -->\n` +
        `<svg xmlns="http://www.w3.org/2000/svg" width="${W}mm" height="${H}mm" viewBox="${-bl} ${-bl} ${W} ${H}">\n` +
        shapes +
        `  <g id="guides-delete-before-export" font-family="Arial, Helvetica, sans-serif" fill="#6b6b6b" text-anchor="middle">\n` +
        `    <text x="${cx}" y="${cy - fs * 0.6}" font-size="${fs}">${name} sticker template</text>\n` +
        `    <text x="${cx}" y="${cy + fs * 0.9}" font-size="${fs * 0.7}">Red: bleed ${bl} mm · Black: trim · Blue: safe area ${sf} mm</text>\n` +
        `  </g>\n` +
        `</svg>\n`
      );
    }

    download(s) {
      if (!s) return;
      const file = s.shape === 'round' ? `real-stickers-template-${s.w}mm-round.svg` : `real-stickers-template-${s.w}x${s.h}mm.svg`;
      const blob = new Blob([this.templateSvg(s)], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file;
      document.body.append(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      if (this.note) this.note.textContent = `Downloaded ${file}`;
    }
  }

  customElements.define('rs-size-guide', RsSizeGuide);
}
